import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  TextField,
  IconButton,
  Button,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toolbar,
} from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import useVoucher from '../../hooks/useVoucher';
import useResourceVoucher from '../../hooks/useResourceVoucher';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VoucherHistory from '../Vouchers/VoucherPaymentHistory';
import VoucherDetailsModal from '../Vouchers/VoucherDetailsModal';
import CreateVoucherModal from '../Vouchers/CreateVoucherModal';
import CreateResourceVoucherModal from '../Vouchers/CreateResourceVoucherModal';
import LockIconWrapper from '../components/LockIconWrapper';
import { useAuth } from '../../context/authContext';
import ResourceVoucherDetailsModal from '../Vouchers/ResourceVoucherDetailsModal';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import { toast } from 'react-toastify';
import EditableList from '../EditableList/EditableList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function VoucherSearch() {
  const { checkRightAny, currentUser } = useAuth();
  // The header search sends the user here with its query when more vouchers
  // match than the suggestion list can show.
  const location = useLocation();
  const initialSearchSeed = location.state?.initialSearch ?? '';
  const {
    getVoucherById,
    getVoucherDownloadURL,
    getVouchersZipURL,
    cancelVoucherById,
    cancelVouchersBatch,
    error: voucherError,
  } = useVoucher();
  const {
    getResourceVoucherById,
    getResourceVoucherDownloadURL,
    getResourceVouchersZipURL,
    cancelResourceVoucherById,
    cancelResourceVouchersBatch,
    error: resourceVoucherError,
  } = useResourceVoucher();
  const [activeTab, setActiveTab] = useState(0); // 0: Wertgutscheine, 1: Produktgutscheine
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const cancelReasonRef = useRef(null);
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [bulkCancelLoading, setBulkCancelLoading] = useState(false);
  const [bulkCancelResult, setBulkCancelResult] = useState(null);
  const bulkCancelReasonRef = useRef(null);

  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfTitle, setPdfTitle] = useState('');

  const statusLabel = useCallback(s => {
    if (s === 'active') return 'Aktiv';
    if (s === 'waitingForPayment') return 'Unbezahlt';
    if (s === 'used') return 'Eingelöst';
    if (s === 'locked') return 'Gesperrt';
    if (s === 'expired') return 'Abgelaufen';
    return s || '';
  }, []);

  const voucherSelectKeys = useMemo(
    () => [
      'voucherCode',
      'status',
      'buyerMail',
      'buyerPostcode',
      'currentBalance',
      'initialBalance',
      'expirationDate',
      'creationComment',
      'relatedVoucherProductId',
      'meta.createdAt',
      'meta.createdBy',
      'meta.createdByString',
    ],
    []
  );

  const resourceVoucherSelectKeys = useMemo(
    () => [
      'voucherCode',
      'status',
      'relatedBookingId',
      'resourceVoucherProductId',
      'durationMS',
      'expirationDate',
      'buyerMail',
      'creationComment',
      'kind',
      'recipientName',
      'meta.createdAt',
      'meta.createdBy',
      'meta.createdByString',
    ],
    []
  );

  useEffect(() => {
    setSelectedIds([]);
  }, [activeTab]);

  // Handler to open history modal and fetch full voucher
  const handleOpenHistory = async row => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const fullVoucher =
        activeTab === 0 ? await getVoucherById(row._id) : await getResourceVoucherById(row._id);
      setSelectedVoucher(fullVoucher);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handler to open voucher details modal
  const handleVoucherClick = voucher => {
    setSelectedVoucherId(voucher._id);
    setDetailsModalOpen(true);
  };

  const dispatchRefresh = useCallback(
    resource => {
      window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource } }));
    },
    []
  );

  const handleVoucherCreated = useCallback(() => {
    dispatchRefresh(activeTab === 0 ? 'voucher' : 'resourceVoucher');
  }, [dispatchRefresh, activeTab]);

  const openCancelDialog = row => {
    setCancelTarget(row);
    setCancelDialogOpen(true);
    setTimeout(() => {
      if (cancelReasonRef.current) {
        cancelReasonRef.current.focus();
      }
    }, 0);
  };

  const closeCancelDialog = () => {
    if (cancelLoading) return;
    setCancelDialogOpen(false);
    setCancelTarget(null);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    if (!checkRightAny('voucher:cancel')) return;

    const reason = cancelReasonRef.current?.value
      ? String(cancelReasonRef.current.value).trim()
      : '';
    setCancelLoading(true);
    try {
      if (activeTab === 0) {
        await cancelVoucherById(cancelTarget._id, reason || null);
      } else {
        await cancelResourceVoucherById(cancelTarget._id, reason || null);
      }
      toast.success('Gutschein wurde storniert und gesperrt');
      closeCancelDialog();
      dispatchRefresh(activeTab === 0 ? 'voucher' : 'resourceVoucher');
    } catch (e) {
      const code = e?.code || e?.response?.data?.code;
      if (code === 'VOUCHER_ALREADY_USED' || code === 'RESOURCE_VOUCHER_ALREADY_USED') {
        toast.error('Gutschein wurde bereits eingelöst und kann nicht storniert werden');
      } else if (code === 'VOUCHER_NOT_ACTIVE' || code === 'RESOURCE_VOUCHER_NOT_ACTIVE') {
        toast.error('Gutschein ist nicht aktiv und kann nicht storniert werden');
      } else {
        toast.error('Fehler beim Stornieren des Gutscheins');
      }
      console.error('Cancel voucher failed:', e);
    } finally {
      setCancelLoading(false);
    }
  };

  // Maps a per-voucher error code of a batch storno to a German reason.
  const bulkCancelErrorMessage = useCallback(code => {
    if (code === 'VOUCHER_ALREADY_USED' || code === 'RESOURCE_VOUCHER_ALREADY_USED')
      return 'Bereits eingelöst';
    if (code === 'VOUCHER_NOT_ACTIVE' || code === 'RESOURCE_VOUCHER_NOT_ACTIVE')
      return 'Nicht aktiv';
    if (code === 'VOUCHER_NOT_FOUND' || code === 'RESOURCE_VOUCHER_NOT_FOUND')
      return 'Gutschein nicht gefunden';
    if (code === 'PAYMENT_NOT_FOUND') return 'Zugehörige Zahlung nicht gefunden';
    if (code === 'PAYMENT_NOT_REFUNDABLE') return 'Zahlung kann nicht erstattet werden';
    if (code === 'PAYMENT_REFUND_LIMIT_EXCEEDED')
      return 'Erstattungsbetrag übersteigt den offenen Zahlungsbetrag';
    return 'Stornierung fehlgeschlagen';
  }, []);

  const openBulkCancelDialog = () => {
    setBulkCancelResult(null);
    setBulkCancelDialogOpen(true);
  };

  const closeBulkCancelDialog = () => {
    if (bulkCancelLoading) return;
    setBulkCancelDialogOpen(false);
    setBulkCancelResult(null);
  };

  const confirmBulkCancel = async () => {
    if (!selectedIds.length) return;
    if (!checkRightAny('voucher:cancel')) return;

    const reason = bulkCancelReasonRef.current?.value
      ? String(bulkCancelReasonRef.current.value).trim()
      : '';
    setBulkCancelLoading(true);
    try {
      const response =
        activeTab === 0
          ? await cancelVouchersBatch(selectedIds, reason || null)
          : await cancelResourceVouchersBatch(selectedIds, reason || null);

      // A request queued for approval returns nothing — close instead of alarming.
      if (!response) {
        setBulkCancelDialogOpen(false);
        return;
      }

      setBulkCancelResult(response);

      const cancelledCount = response.cancelledCount || 0;
      const failedCount = response.failedCount || 0;
      if (cancelledCount > 0) {
        toast.success(
          `${cancelledCount} ${cancelledCount === 1 ? 'Gutschein wurde' : 'Gutscheine wurden'} storniert und gesperrt`
        );
      }
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} ${failedCount === 1 ? 'Gutschein wurde' : 'Gutscheine wurden'} übersprungen`
        );
      }

      // Cancelled vouchers stay in the list as "Gesperrt", so they leave the
      // selection instead of being offered for a second storno.
      const cancelledIds = (response.results || []).filter(r => r.success).map(r => r.voucherId);
      setSelectedIds(prev => prev.filter(id => !cancelledIds.includes(id)));
      dispatchRefresh(activeTab === 0 ? 'voucher' : 'resourceVoucher');
    } catch (e) {
      toast.error('Fehler beim Stornieren der Gutscheine');
      console.error('Bulk cancel vouchers failed:', e);
    } finally {
      setBulkCancelLoading(false);
    }
  };

  const handleDownloadVoucher = useCallback(
    async raw => {
      if (!raw) return;
      if (raw.status === 'waitingForPayment') {
        toast.error('Gutschein muss erst bezahlt werden, bevor er heruntergeladen werden kann');
        return;
      }

      const toastMessage = `Die Download-Anfrage für Gutschein ${raw.voucherCode} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
      const displayInfo = {
        Typ: 'Gutschein PDF Download',
        Gutscheincode: raw.voucherCode,
        Status: statusLabel(raw.status),
        Restguthaben: `${raw.currentBalance?.sum?.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} €`,
      };

      const onStored = response => {
        if (response?.preSignedURL) window.open(response.preSignedURL, '_blank');
      };

      try {
        const response = await getVoucherDownloadURL(raw._id, toastMessage, onStored, displayInfo);
        if (response?.preSignedURL) window.open(response.preSignedURL, '_blank');
      } catch (err) {
        toast.error('Fehler beim Download des Gutscheins');
        console.error('Unexpected error during voucher download:', err);
      }
    },
    [getVoucherDownloadURL, statusLabel]
  );

  const handleDownloadResourceVoucher = useCallback(
    async raw => {
      if (!raw) return;
      if (raw.status === 'waitingForPayment') {
        toast.error('Gutschein muss erst bezahlt werden, bevor er heruntergeladen werden kann');
        return;
      }

      const toastMessage = `Die Download-Anfrage für Gutschein ${raw.voucherCode} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
      const displayInfo = {
        Typ: 'Produktgutschein PDF Download',
        Gutscheincode: raw.voucherCode,
        Status: statusLabel(raw.status),
      };

      const onStored = response => {
        if (response?.preSignedURL) window.open(response.preSignedURL, '_blank');
      };

      try {
        const response = await getResourceVoucherDownloadURL(
          raw._id,
          toastMessage,
          onStored,
          displayInfo
        );
        if (response?.preSignedURL) window.open(response.preSignedURL, '_blank');
      } catch (err) {
        toast.error('Fehler beim Download des Gutscheins');
        console.error('Unexpected error during resource voucher download:', err);
      }
    },
    [getResourceVoucherDownloadURL, statusLabel]
  );

  const handlePreviewVoucherPdf = useCallback(
    async raw => {
      if (!raw) return;
      if (raw.status === 'waitingForPayment') {
        toast.error('Gutschein muss erst bezahlt werden, bevor er angezeigt werden kann');
        return;
      }

      const toastMessage = `Die Anzeige-Anfrage für Gutschein ${raw.voucherCode} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
      const displayInfo = {
        Typ: 'Gutschein PDF Anzeige',
        Gutscheincode: raw.voucherCode,
        Status: statusLabel(raw.status),
      };

      const onStored = response => {
        if (response?.preSignedURL) {
          setPdfTitle(`Gutschein ${raw.voucherCode}`);
          setPdfUrl(`${response.preSignedURL}${response.preSignedURL.includes('?') ? '&' : '?'}disposition=inline`);
          setPdfViewerOpen(true);
        }
      };

      try {
        const response = await getVoucherDownloadURL(raw._id, toastMessage, onStored, displayInfo);
        if (response?.preSignedURL) {
          setPdfTitle(`Gutschein ${raw.voucherCode}`);
          setPdfUrl(`${response.preSignedURL}${response.preSignedURL.includes('?') ? '&' : '?'}disposition=inline`);
          setPdfViewerOpen(true);
        }
      } catch (err) {
        toast.error('Fehler beim Anzeigen des Gutscheins');
        console.error('Unexpected error during voucher pdf preview:', err);
      }
    },
    [getVoucherDownloadURL, statusLabel]
  );

  const handlePreviewResourceVoucherPdf = useCallback(
    async raw => {
      if (!raw) return;
      if (raw.status === 'waitingForPayment') {
        toast.error('Gutschein muss erst bezahlt werden, bevor er angezeigt werden kann');
        return;
      }

      const toastMessage = `Die Anzeige-Anfrage für Gutschein ${raw.voucherCode} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
      const displayInfo = {
        Typ: 'Produktgutschein PDF Anzeige',
        Gutscheincode: raw.voucherCode,
        Status: statusLabel(raw.status),
      };

      const onStored = response => {
        if (response?.preSignedURL) {
          setPdfTitle(`Gutschein ${raw.voucherCode}`);
          setPdfUrl(`${response.preSignedURL}${response.preSignedURL.includes('?') ? '&' : '?'}disposition=inline`);
          setPdfViewerOpen(true);
        }
      };

      try {
        const response = await getResourceVoucherDownloadURL(
          raw._id,
          toastMessage,
          onStored,
          displayInfo
        );
        if (response?.preSignedURL) {
          setPdfTitle(`Gutschein ${raw.voucherCode}`);
          setPdfUrl(`${response.preSignedURL}${response.preSignedURL.includes('?') ? '&' : '?'}disposition=inline`);
          setPdfViewerOpen(true);
        }
      } catch (err) {
        toast.error('Fehler beim Anzeigen des Gutscheins');
        console.error('Unexpected error during resource voucher pdf preview:', err);
      }
    },
    [getResourceVoucherDownloadURL, statusLabel]
  );

  // Batch ZIP download
  const handleBatchZipDownload = async () => {
    if (selectedIds.length < 2) return;

    const isResourceVoucherTab = activeTab === 1;
    try {
      const toastMessage = `Die Download-Anfrage für ${selectedIds.length} PDFs wurde gespeichert`;
      const displayInfo = {
        Typ: isResourceVoucherTab ? 'Produktgutscheine ZIP Download' : 'Gutscheine ZIP Download',
        Anzahl: selectedIds.length.toString(),
      };
      const onStored = response => {
        if (response && response.preSignedURL) {
          window.open(response.preSignedURL, '_blank');
        }
      };
      const response = isResourceVoucherTab
        ? await getResourceVouchersZipURL(selectedIds, toastMessage, onStored, displayInfo)
        : await getVouchersZipURL(selectedIds, toastMessage, onStored, displayInfo);
      if (response && response.preSignedURL) {
        window.open(response.preSignedURL, '_blank');
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen des ZIP-Downloads');
      console.error('Batch ZIP download failed:', e);
    }
  };

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: 'Aktiv' },
      { value: 'waitingForPayment', label: 'Unbezahlt' },
      { value: 'used', label: 'Eingelöst' },
      { value: 'locked', label: 'Gesperrt' },
      { value: 'expired', label: 'Abgelaufen' },
    ],
    []
  );

  const resourceVoucherKindOptions = useMemo(
    () => [
      { value: 'standard', label: 'Standard' },
      { value: 'free-game', label: 'Freispiel' },
    ],
    []
  );

  const voucherSchema = useMemo(
    () => ({
      voucherCode: { label: 'Gutscheincode', type: 'text', required: false, minWidth: '160px', readOnly: true },
      buyerMail: { label: 'E-Mail', type: 'text', required: false, minWidth: '220px', readOnly: true },
      buyerPostcode: { label: 'PLZ', type: 'text', required: false, minWidth: '90px', readOnly: true },
      status: { label: 'Status', type: 'text', filterType: 'select', options: statusOptions, required: false, minWidth: '140px', readOnly: true },
      currentBalance: { label: 'Restguthaben', type: 'price', required: false, minWidth: '140px', readOnly: true },
      initialBalance: { label: 'Urspr. Wert', type: 'price', required: false, minWidth: '140px', readOnly: true },
      expirationDate: { label: 'Ablaufdatum', type: 'date', required: false, minWidth: '120px', readOnly: true },
      createdAt: { label: 'Erstellt am', type: 'date', required: false, minWidth: '120px', readOnly: true },
      createdBy: { label: 'Erstellt von', type: 'text', required: false, minWidth: '160px', readOnly: true },
      product: { label: 'Produkt', type: 'text', required: false, minWidth: '240px', readOnly: true },
      creationComment: { label: 'Kommentar', type: 'text', required: false, minWidth: '240px', readOnly: true },
      __raw: { label: '__raw', type: 'object', excludeFromList: true },
    }),
    [statusOptions]
  );

  const resourceVoucherSchema = useMemo(
    () => ({
      voucherCode: { label: 'Code', type: 'text', required: false, minWidth: '160px', readOnly: true },
      buyerMail: { label: 'E-Mail', type: 'text', required: false, minWidth: '220px', readOnly: true },
      status: { label: 'Status', type: 'text', filterType: 'select', options: statusOptions, required: false, minWidth: '140px', readOnly: true },
      kind: { label: 'Art', type: 'text', filterType: 'select', options: resourceVoucherKindOptions, required: false, minWidth: '110px', readOnly: true },
      product: { label: 'Produkt', type: 'text', required: false, minWidth: '240px', readOnly: true },
      durationMin: { label: 'Dauer (Min)', type: 'number', required: false, minWidth: '110px', readOnly: true },
      expirationDate: { label: 'Ablaufdatum', type: 'date', required: false, minWidth: '120px', readOnly: true },
      createdAt: { label: 'Erstellt am', type: 'date', required: false, minWidth: '120px', readOnly: true },
      createdBy: { label: 'Erstellt von', type: 'text', required: false, minWidth: '160px', readOnly: true },
      creationComment: { label: 'Kommentar', type: 'text', required: false, minWidth: '240px', readOnly: true },
      __raw: { label: '__raw', type: 'object', excludeFromList: true },
    }),
    [statusOptions, resourceVoucherKindOptions]
  );

  const listConfig = useMemo(
    () => ({
      searchParam: 'searchQuery',
      resultsKey: 'results',
    }),
    []
  );

  const voucherListParams = useMemo(() => {
    const params = { selectKeys: voucherSelectKeys.join(',') };
    if (onlyMine) params.onlyMine = 'true';
    return params;
  }, [voucherSelectKeys, onlyMine]);

  const resourceVoucherListParams = useMemo(() => {
    const params = { selectKeys: resourceVoucherSelectKeys.join(',') };
    if (onlyMine) params.onlyMine = 'true';
    return params;
  }, [resourceVoucherSelectKeys, onlyMine]);

  const transformVoucherItem = useCallback(
    v => {
      const createdBy =
        v?.meta?.createdBy?.firstName && v?.meta?.createdBy?.lastName
          ? `${v.meta.createdBy.firstName} ${v.meta.createdBy.lastName}`
          : v?.meta?.createdBy?.username || v?.meta?.createdByString || '-';
      const product = v?.relatedVoucherProductId
        ? `${v.relatedVoucherProductId.name} (${v.relatedVoucherProductId.branchId?.longName || 'Unbekannte Filiale'})`
        : '-';
      return {
        ...v,
        status: statusLabel(v?.status),
        currentBalance: v?.currentBalance?.sum ?? null,
        initialBalance: v?.initialBalance?.sum ?? null,
        createdAt: v?.meta?.createdAt ?? null,
        createdBy,
        product,
        __raw: v,
      };
    },
    [statusLabel]
  );

  const transformResourceVoucherItem = useCallback(
    v => {
      const createdBy =
        v?.meta?.createdBy?.firstName && v?.meta?.createdBy?.lastName
          ? `${v.meta.createdBy.firstName} ${v.meta.createdBy.lastName}`
          : v?.meta?.createdBy?.username || v?.meta?.createdByString || '-';
      const product = v?.resourceVoucherProductId?.name || '-';
      return {
        ...v,
        status: statusLabel(v?.status),
        // Vouchers created before the `kind` field existed have no value and are standard.
        kind: v?.kind === 'free-game' ? 'Freispiel' : 'Standard',
        product,
        durationMin: Math.round((v?.durationMS || 0) / 60000),
        createdAt: v?.meta?.createdAt ?? null,
        createdBy,
        __raw: v,
      };
    },
    [statusLabel]
  );

  const renderRowActions = useCallback(
    row => {
      const raw = row?.__raw;
      if (!raw) return null;

      const isCreator = raw?.meta?.createdBy?._id === currentUser?.userId;
      const canDownload = checkRightAny('voucher:special:download') || isCreator;
      const canCancel = checkRightAny('voucher:cancel') && raw.status === 'active';

      return (
        <>
          <Tooltip title='Zahlungsdetails'>
            <IconButton
              size='small'
              onClick={e => {
                e.stopPropagation();
                handleOpenHistory(raw);
              }}
            >
              <HistoryIcon fontSize='small' />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              !canDownload
                ? 'Keine Berechtigung zum Anzeigen'
                : raw.status === 'waitingForPayment'
                  ? 'Gutschein muss erst bezahlt werden'
                  : 'PDF anzeigen'
            }
          >
            <span>
              <IconButton
                size='small'
                onClick={e => {
                  e.stopPropagation();
                  if (activeTab === 0) handlePreviewVoucherPdf(raw);
                  else handlePreviewResourceVoucherPdf(raw);
                }}
                disabled={raw.status === 'waitingForPayment' || !canDownload}
              >
                {isCreator ? (
                  <VisibilityIcon fontSize='small' />
                ) : (
                  <LockIconWrapper right='voucher:special:download'>
                    <VisibilityIcon fontSize='small' />
                  </LockIconWrapper>
                )}
              </IconButton>
            </span>
          </Tooltip>

          {activeTab === 1 && (
            <Tooltip title={raw.relatedBookingId ? 'Buchung öffnen' : 'Keine Buchung'}>
              <span>
                <IconButton
                  size='small'
                  disabled={!raw.relatedBookingId}
                  onClick={e => {
                    e.stopPropagation();
                    if (!raw.relatedBookingId) return;
                    setSelectedBookingId(raw.relatedBookingId);
                    setBookingModalOpen(true);
                  }}
                >
                  <OpenInNewIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <Tooltip
            title={
              !canDownload
                ? 'Keine Berechtigung zum Download'
                : raw.status === 'waitingForPayment'
                  ? 'Gutschein muss erst bezahlt werden'
                  : 'PDF herunterladen'
            }
          >
            <span>
              <IconButton
                size='small'
                onClick={e => {
                  e.stopPropagation();
                  if (activeTab === 0) handleDownloadVoucher(raw);
                  else handleDownloadResourceVoucher(raw);
                }}
                disabled={raw.status === 'waitingForPayment' || !canDownload}
              >
                {isCreator ? (
                  <GetAppIcon fontSize='small' />
                ) : (
                  <LockIconWrapper right='voucher:special:download'>
                    <GetAppIcon fontSize='small' />
                  </LockIconWrapper>
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              !checkRightAny('voucher:cancel')
                ? 'Keine Berechtigung zum Stornieren'
                : raw.status !== 'active'
                  ? 'Nur aktive Gutscheine können storniert werden'
                  : 'Gutschein stornieren (Refund + Sperre)'
            }
          >
            <span>
              <IconButton
                size='small'
                disabled={!canCancel}
                onClick={e => {
                  e.stopPropagation();
                  openCancelDialog(raw);
                }}
              >
                <LockIconWrapper right='voucher:cancel'>
                  <CancelIcon fontSize='small' />
                </LockIconWrapper>
              </IconButton>
            </span>
          </Tooltip>
        </>
      );
    },
    [
      activeTab,
      checkRightAny,
      currentUser?.userId,
      handleDownloadVoucher,
      handleDownloadResourceVoucher,
      handlePreviewVoucherPdf,
      handlePreviewResourceVoucherPdf,
      setSelectedBookingId,
      setBookingModalOpen,
    ]
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2 }}>
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label='Wertgutscheine' />
        <Tab label='Produktgutscheine' />
      </Tabs>

      {activeTab === 0 ? (
        <EditableList
          title='Wertgutscheine'
          resourceType='voucher'
          schema={voucherSchema}
          searchPlaceholder='Gutscheine durchsuchen (Code, Mail, PLZ)'
          initialSearchTerm={initialSearchSeed}
          listParams={voucherListParams}
          renderRowActions={renderRowActions}
          onRowClick={row => handleVoucherClick(row?.__raw || row)}
          selection={{
            enabled: true,
            selectedIds,
            onSelectedIdsChange: setSelectedIds,
            getRowId: row => row?._id,
          }}
          options={{
            disableDelete: true,
            disableDetailView: true,
            listConfig,
            titleKey: 'voucherCode',
            transformItems: transformVoucherItem,
            addButtonLabel: 'NEU',
            onCreateClick: () => setCreateModalOpen(true),
          }}
          renderTopControlsStart={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 }}>
                <Typography variant='caption' sx={{ textAlign: 'center' }}>
                  Meine
                </Typography>
                <Switch checked={onlyMine} onChange={e => setOnlyMine(e.target.checked)} size='small' />
              </Box>
            </Box>
          )}
          renderTopControlsEnd={() => (
            <>
              {checkRightAny('voucher:cancel') && (
                <Button
                  variant='outlined'
                  color='error'
                  startIcon={<CancelIcon />}
                  onClick={openBulkCancelDialog}
                  disabled={selectedIds.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Stornieren{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                </Button>
              )}
              <Button
                variant='outlined'
                color='primary'
                startIcon={<GetAppIcon />}
                onClick={handleBatchZipDownload}
                disabled={selectedIds.length < 2}
                sx={{ whiteSpace: 'nowrap' }}
              >
                ZIP herunterladen
              </Button>
            </>
          )}
        />
      ) : (
        <EditableList
          title='Produktgutscheine'
          resourceType='resourceVoucher'
          schema={resourceVoucherSchema}
          searchPlaceholder='Produktgutscheine durchsuchen (Code, Mail)'
          initialSearchTerm={initialSearchSeed}
          listParams={resourceVoucherListParams}
          renderRowActions={renderRowActions}
          onRowClick={row => handleVoucherClick(row?.__raw || row)}
          selection={{
            enabled: true,
            selectedIds,
            onSelectedIdsChange: setSelectedIds,
            getRowId: row => row?._id,
          }}
          options={{
            disableDelete: true,
            disableDetailView: true,
            listConfig,
            rightsResourceType: 'voucher',
            titleKey: 'voucherCode',
            transformItems: transformResourceVoucherItem,
            addButtonLabel: 'NEU',
            onCreateClick: () => setCreateModalOpen(true),
          }}
          renderTopControlsStart={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 }}>
                <Typography variant='caption' sx={{ textAlign: 'center' }}>
                  Meine
                </Typography>
                <Switch checked={onlyMine} onChange={e => setOnlyMine(e.target.checked)} size='small' />
              </Box>
            </Box>
          )}
          renderTopControlsEnd={() => (
            <>
              {checkRightAny('voucher:cancel') && (
                <Button
                  variant='outlined'
                  color='error'
                  startIcon={<CancelIcon />}
                  onClick={openBulkCancelDialog}
                  disabled={selectedIds.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Stornieren{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                </Button>
              )}
              <Button
                variant='outlined'
                color='primary'
                startIcon={<GetAppIcon />}
                onClick={handleBatchZipDownload}
                disabled={selectedIds.length < 2}
                sx={{ whiteSpace: 'nowrap' }}
              >
                ZIP herunterladen
              </Button>
            </>
          )}
        />
      )}
      {(voucherError || resourceVoucherError) && (
        <Typography color='error' sx={{ mt: 2 }}>
          Fehler: {voucherError || resourceVoucherError}
        </Typography>
      )}
      <VoucherHistory
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setSelectedVoucher(null);
        }}
        voucher={selectedVoucher}
        loading={historyLoading}
      />
      <VoucherDetailsModal
        open={detailsModalOpen && activeTab === 0}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedVoucherId(null);
        }}
        voucherId={selectedVoucherId}
      />
      <ResourceVoucherDetailsModal
        open={detailsModalOpen && activeTab === 1}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedVoucherId(null);
        }}
        voucherId={selectedVoucherId}
      />
      <BookingDetailsModal
        open={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        onUpdate={() => {}}
      />
      {activeTab === 0 ? (
        <CreateVoucherModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleVoucherCreated}
        />
      ) : (
        <CreateResourceVoucherModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleVoucherCreated}
        />
      )}
      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Storno bestätigen</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Du bist dabei, den Gutschein{' '}
            <Box component='span' sx={{ fontWeight: 600 }}>
              {cancelTarget?.voucherCode || cancelTarget?._id}
            </Box>{' '}
            zu stornieren.
          </Typography>
          <Typography sx={{ mb: 1 }}>Dabei passiert Folgendes:</Typography>
          <Box component='ul' sx={{ mt: 0, mb: 2, pl: 2.5 }}>
            <li>
              <Typography component='span'>
                Es wird eine{' '}
                <Box component='span' sx={{ fontWeight: 600 }}>
                  Rückerstattung der Zahlung
                </Box>{' '}
                ausgelöst (falls online bezahlt).
              </Typography>
            </li>
            <li>
              <Typography component='span'>
                Der Gutschein wird{' '}
                <Box component='span' sx={{ fontWeight: 600 }}>
                  gesperrt
                </Box>{' '}
                und kann danach{' '}
                <Box component='span' sx={{ fontWeight: 600 }}>
                  nicht mehr verwendet
                </Box>{' '}
                werden.
              </Typography>
            </li>
          </Box>
          <Typography variant='body2' sx={{ mb: 2 }} color='text.secondary'>
            Hinweis: Storno ist nur möglich, solange der Gutschein noch nicht eingelöst wurde. Die
            Rückerstattung kann je nach Zahlungsanbieter etwas Zeit benötigen.
          </Typography>
          <TextField
            label='Grund (optional)'
            fullWidth
            margin='dense'
            inputRef={cancelReasonRef}
            key={cancelTarget?._id || 'cancel-reason'}
            defaultValue={
              cancelTarget ? `Storno Gutschein ${cancelTarget.voucherCode || cancelTarget._id}` : ''
            }
            disabled={cancelLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={cancelLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={confirmCancel}
            variant='contained'
            color='error'
            disabled={cancelLoading || !cancelTarget || !checkRightAny('voucher:cancel')}
          >
            Storno ausführen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkCancelDialogOpen} onClose={closeBulkCancelDialog} maxWidth='sm' fullWidth>
        <DialogTitle>
          {bulkCancelResult ? 'Storno abgeschlossen' : 'Mehrere Gutscheine stornieren'}
        </DialogTitle>
        <DialogContent>
          {bulkCancelResult ? (
            <>
              <Typography sx={{ mb: 1.5 }}>
                {bulkCancelResult.cancelledCount || 0} storniert,{' '}
                {bulkCancelResult.failedCount || 0} übersprungen.
              </Typography>
              {(bulkCancelResult.results || []).some(r => !r.success) && (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {(bulkCancelResult.results || [])
                    .filter(r => !r.success)
                    .map(r => (
                      <Box
                        key={r.voucherId}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          px: 1.5,
                          py: 1,
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:last-of-type': { borderBottom: 0 },
                        }}
                      >
                        <Typography sx={{ fontFamily: 'monospace' }}>
                          {r.voucherCode || r.voucherId}
                        </Typography>
                        <Typography color='text.secondary' sx={{ textAlign: 'right' }}>
                          {bulkCancelErrorMessage(r.code)}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography sx={{ mb: 1.5 }}>
                Du bist dabei,{' '}
                <Box component='span' sx={{ fontWeight: 600 }}>
                  {selectedIds.length}
                </Box>{' '}
                {selectedIds.length === 1 ? 'Gutschein' : 'Gutscheine'} zu stornieren.
              </Typography>
              <Typography sx={{ mb: 1 }}>Dabei passiert Folgendes:</Typography>
              <Box component='ul' sx={{ mt: 0, mb: 2, pl: 2.5 }}>
                <li>
                  <Typography component='span'>
                    Online bezahlte Beträge werden{' '}
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      zurückerstattet
                    </Box>
                    .
                  </Typography>
                </li>
                <li>
                  <Typography component='span'>
                    Die Gutscheine werden{' '}
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      gesperrt
                    </Box>{' '}
                    und können danach nicht mehr eingelöst werden.
                  </Typography>
                </li>
                <li>
                  <Typography component='span'>
                    Nur aktive Gutscheine werden storniert — bereits eingelöste, gesperrte und
                    unbezahlte werden übersprungen.
                  </Typography>
                </li>
              </Box>
              <TextField
                label='Grund (optional)'
                fullWidth
                margin='dense'
                inputRef={bulkCancelReasonRef}
                defaultValue='Storno durch Mitarbeiter'
                disabled={bulkCancelLoading}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {bulkCancelResult ? (
            <Button onClick={closeBulkCancelDialog} variant='contained'>
              Schließen
            </Button>
          ) : (
            <>
              <Button onClick={closeBulkCancelDialog} disabled={bulkCancelLoading}>
                Abbrechen
              </Button>
              <Button
                onClick={confirmBulkCancel}
                variant='contained'
                color='error'
                disabled={
                  bulkCancelLoading ||
                  selectedIds.length === 0 ||
                  !checkRightAny('voucher:cancel')
                }
              >
                Storno ausführen
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={pdfViewerOpen}
        onClose={() => {
          setPdfViewerOpen(false);
          setPdfUrl(null);
          setPdfTitle('');
        }}
        maxWidth='lg'
        fullWidth
        PaperProps={{ sx: { height: '85vh' } }}
      >
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              {pdfTitle || 'PDF Vorschau'}
            </Typography>
            <IconButton
              edge='end'
              color='inherit'
              onClick={() => {
                setPdfViewerOpen(false);
                setPdfUrl(null);
                setPdfTitle('');
              }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 0, height: 'calc(85vh - 64px)' }}>
          {pdfUrl && (
            <iframe
              src={`${pdfUrl}#toolbar=0&navpanes=0`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title='PDF Viewer'
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

export default VoucherSearch;
